import { route, error, type DemoRoute } from '../router';
import type {
  AutoMatchResponse,
  BankStatementProcessingStatus,
  BulkCommitRequest,
  BulkCommitResponse,
  CommitResult,
  FileDeleteCheckResponse,
  FileDeleteResponse,
  FileReprocessResponse,
  InvoiceData,
  InvoicesResponse,
  MatchedInvoiceData,
  MatchedPairData,
  MatchedPairsWithPreviewResponse,
  MatchedTransactionData,
  MatchRequest,
  MatchResponse,
  MatchUpdateRequest,
  ProcessedFilesResponse,
  ReconcileConfirmResponse,
  TransactionData,
  TransactionDeleteRequest,
  TransactionDeleteResponse,
  TransactionsResponse,
  UpdateLinkAccountsRequest,
  UploadBankStatementResponse,
} from '../../lib/api';
import {
  BANK_IBAN,
  BANK_NAME,
  buildPreview,
  CLIENT_ID,
  CLIENT_NAME,
  files,
  invoices,
  JULY_TRANSACTIONS_COUNT,
  type LinkRecord,
  links,
  nextAccrecId,
  nextId,
  nowISO,
  seedJulyImport,
  transactions,
  type TxRecord,
  type InvRecord,
} from '../data/reconciliation';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type MatchStatusFilter = 'matched' | 'unmatched' | 'committed' | 'all';

const linkForTransaction = (txId: string): LinkRecord | undefined =>
  links.find((l) => l.transaction_id === txId);

const linksForInvoice = (invId: string): LinkRecord[] => links.filter((l) => l.invoice_id === invId);

const statusMatches = (link: LinkRecord | undefined, filter: MatchStatusFilter | null): boolean => {
  if (!filter || filter === 'all') return true;
  if (filter === 'unmatched') return !link;
  if (filter === 'committed') return !!link?.is_committed;
  if (filter === 'matched') return !!link && !link.is_committed;
  return true;
};

const toMatchResponse = (link: LinkRecord): MatchResponse => {
  const tx = transactions.find((t) => t.id === link.transaction_id);
  const inv = invoices.find((i) => i.id === link.invoice_id);
  return {
    id: link.id,
    invoice_id: link.invoice_id,
    transaction_id: link.transaction_id,
    match_percentage: link.match_percentage,
    match_reason: link.match_reason,
    notes: link.notes,
    status: link.status,
    is_automatic: link.is_automatic,
    linked_at: link.linked_at,
    linked_by_user_id: null,
    lock_date: null,
    created_at: link.linked_at,
    updated_at: link.committed_at ?? link.linked_at,
    transaction_summary: tx
      ? {
          id: tx.id,
          amount: tx.amount,
          currency: tx.currency,
          booking_ts: tx.booking_ts,
          description: tx.description,
          reference: tx.reference,
          counterparty_name: tx.counterparty_name,
        }
      : null,
    invoice_summary: inv
      ? { id: inv.id, client_id: inv.client_id, status: inv.status, filename: inv.filename, original_filename: inv.filename }
      : null,
    mirage_accrec_id: link.mirage_accrec_id,
    pushed_to_mirage_at: link.committed_at,
    reversed_at: null,
  };
};

const toTransactionData = (tx: TxRecord): TransactionData => {
  const link = linkForTransaction(tx.id);
  return {
    id: tx.id,
    source: tx.source,
    description: tx.description,
    reference: tx.reference,
    amount: tx.amount,
    booking_ts: tx.booking_ts,
    counterparty_name: tx.counterparty_name,
    currency: tx.currency,
    client_id: tx.client_id,
    client_name: tx.client_name,
    links: link ? [toMatchResponse(link)] : [],
  };
};

const toInvoiceData = (inv: InvRecord): InvoiceData => ({
  id: inv.id,
  invoice_number: inv.invoice_number,
  client_name: inv.client_name,
  client_id: inv.client_id,
  total_amount: inv.total_amount,
  currency: inv.currency,
  invoice_date: inv.invoice_date,
  status: inv.status,
  vendor: inv.vendor,
  customer: inv.customer,
  links: linksForInvoice(inv.id).map(toMatchResponse),
});

const toDMY = (iso: string): string => {
  const datePart = iso.slice(0, 10);
  const [y, m, d] = datePart.split('-');
  return `${d}.${m}.${y}`;
};

const toMatchedPairData = (link: LinkRecord): MatchedPairData | null => {
  const tx = transactions.find((t) => t.id === link.transaction_id);
  const inv = invoices.find((i) => i.id === link.invoice_id);
  if (!tx || !inv) return null;

  const outgoing = tx.amount < 0;
  const transaction: MatchedTransactionData = {
    id: tx.id,
    reference: tx.reference,
    from: outgoing ? CLIENT_NAME : tx.counterparty_name,
    to: outgoing ? tx.counterparty_name : CLIENT_NAME,
    date: toDMY(tx.booking_ts),
    amount: tx.amount.toFixed(2),
    purpose: tx.description,
    matched_invoice: inv.invoice_number,
  };

  const invoice: MatchedInvoiceData = {
    id: inv.id,
    filename: inv.filename,
    invoice_date: toDMY(inv.invoice_date),
    invoice_number: inv.invoice_number,
    mirage_invoice_number: inv.mirage_invoice_number,
    mirage_counterparty_id: inv.mirage_counterparty_id,
    vendor: inv.vendor.name,
    customer: inv.customer.name,
    amount: inv.total_amount.toFixed(2),
    type: inv.type,
    matched_transaction: tx.reference,
  };

  return {
    link_id: link.id,
    match_percentage: link.match_percentage,
    match_success: link.match_success,
    is_committed: link.is_committed,
    committed_at: link.committed_at,
    mirage_accrec_id: link.mirage_accrec_id,
    transaction,
    invoice,
    preview: link.preview,
  };
};

const parseClientIds = (query: URLSearchParams): string[] => query.getAll('client_ids');

const matchesClient = (clientId: string, clientIds: string[]): boolean =>
  clientIds.length === 0 || clientIds.includes(clientId);

const applySearch = (haystacks: (string | null | undefined)[], term: string): boolean =>
  haystacks.some((h) => (h ?? '').toLowerCase().includes(term));

// ---------------------------------------------------------------------------
// Upload job tracking (scripted "import" that skips the file dialog)
// ---------------------------------------------------------------------------

interface UploadJob {
  fileId: string;
  startedAt: number;
  durationMs: number;
  finalized: boolean;
}

const uploadJobs = new Map<string, UploadJob>();

const jobProgress = (job: UploadJob): { status: 'processing' | 'completed'; transactionsCount: number } => {
  const elapsed = Date.now() - job.startedAt;
  if (elapsed >= job.durationMs) {
    if (!job.finalized) {
      job.finalized = true;
      seedJulyImport(job.fileId);
      const fileRecord = files.find((f) => f.id === job.fileId);
      if (fileRecord) {
        fileRecord.transaction_count = JULY_TRANSACTIONS_COUNT;
        fileRecord.status = 'completed';
      }
    }
    return { status: 'completed', transactionsCount: JULY_TRANSACTIONS_COUNT };
  }
  // Keep the count at 0 while processing: UploadBankStatement treats
  // transactions_count > 0 as "completed" and would stop polling early.
  return { status: 'processing', transactionsCount: 0 };
};

// Advance any in-flight upload jobs on every read, so the import completes
// even if the UI stops polling the status endpoint early.
const tickUploadJobs = (): void => {
  uploadJobs.forEach((job) => jobProgress(job));
};

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

export const reconciliationHandlers: DemoRoute[] = [
  // --- Transactions -----------------------------------------------------
  route('GET', '/reconciliation/transactions', (req) => {
    tickUploadJobs();
    const matchStatus = (req.query.get('match_status') as MatchStatusFilter | null) ?? null;
    const clientIds = parseClientIds(req.query);
    const search = (req.query.get('search') ?? '').toLowerCase().trim();
    const dateFrom = req.query.get('date_from');
    const dateTo = req.query.get('date_to');
    const amountMin = req.query.get('amount_min');
    const amountMax = req.query.get('amount_max');
    const skip = Number(req.query.get('skip') ?? 0);
    const limit = Number(req.query.get('limit') ?? 100);

    let filtered = transactions.filter((tx) => matchesClient(tx.client_id, clientIds));
    filtered = filtered.filter((tx) => statusMatches(linkForTransaction(tx.id), matchStatus));
    if (search) {
      filtered = filtered.filter((tx) => applySearch([tx.reference, tx.description, tx.counterparty_name], search));
    }
    if (dateFrom) filtered = filtered.filter((tx) => tx.booking_ts.slice(0, 10) >= dateFrom);
    if (dateTo) filtered = filtered.filter((tx) => tx.booking_ts.slice(0, 10) <= dateTo);
    if (amountMin) filtered = filtered.filter((tx) => tx.amount >= Number(amountMin));
    if (amountMax) filtered = filtered.filter((tx) => tx.amount <= Number(amountMax));

    filtered = [...filtered].sort((a, b) => b.booking_ts.localeCompare(a.booking_ts));

    const total = filtered.length;
    const page = filtered.slice(skip, skip + limit);

    const response: TransactionsResponse = {
      transactions: page.map(toTransactionData),
      total,
      company_name: CLIENT_NAME,
    };
    return response;
  }),

  // --- Invoices -----------------------------------------------------------
  route('GET', '/reconciliation/invoices', (req) => {
    tickUploadJobs();
    const matchStatus = (req.query.get('match_status') as MatchStatusFilter | null) ?? null;
    const clientIds = parseClientIds(req.query);
    const search = (req.query.get('search') ?? '').toLowerCase().trim();
    const dateFrom = req.query.get('date_from');
    const dateTo = req.query.get('date_to');
    const skip = Number(req.query.get('skip') ?? 0);
    const limit = Number(req.query.get('limit') ?? 100);

    let filtered = invoices.filter((inv) => matchesClient(inv.client_id, clientIds));
    filtered = filtered.filter((inv) => {
      // An invoice may have zero or more links (partial matches); use the
      // first one to classify committed/matched/unmatched for filtering.
      const link = linksForInvoice(inv.id)[0];
      return statusMatches(link, matchStatus);
    });
    if (search) {
      filtered = filtered.filter((inv) =>
        applySearch([inv.invoice_number, inv.filename, inv.vendor.name, inv.customer.name], search)
      );
    }
    if (dateFrom) filtered = filtered.filter((inv) => inv.invoice_date >= dateFrom);
    if (dateTo) filtered = filtered.filter((inv) => inv.invoice_date <= dateTo);

    filtered = [...filtered].sort((a, b) => b.invoice_date.localeCompare(a.invoice_date));

    const total = filtered.length;
    const page = filtered.slice(skip, skip + limit);

    const response: InvoicesResponse = {
      invoices: page.map(toInvoiceData),
      total,
      company_name: CLIENT_NAME,
    };
    return response;
  }),

  // --- Matched pairs with preview ------------------------------------------
  route('GET', '/reconciliation/matched-pairs-with-preview', (req) => {
    tickUploadJobs();
    const matchStatus = (req.query.get('match_status') as MatchStatusFilter | null) ?? 'all';
    const clientIds = parseClientIds(req.query);
    const search = (req.query.get('search') ?? '').toLowerCase().trim();
    const skip = Number(req.query.get('skip') ?? 0);
    const limit = Number(req.query.get('limit') ?? 100);

    let filtered = links.filter((link) => {
      const tx = transactions.find((t) => t.id === link.transaction_id);
      return tx ? matchesClient(tx.client_id, clientIds) : false;
    });
    filtered = filtered.filter((link) => statusMatches(link, matchStatus === 'unmatched' ? null : matchStatus));

    const pairs = filtered.map(toMatchedPairData).filter((p): p is MatchedPairData => p !== null);

    const searched = search
      ? pairs.filter((p) =>
          applySearch(
            [p.transaction.reference, p.transaction.purpose, p.invoice.invoice_number, p.invoice.vendor, p.invoice.customer],
            search
          )
        )
      : pairs;

    const total = searched.length;
    const page = searched.slice(skip, skip + limit);

    const response: MatchedPairsWithPreviewResponse = {
      matched_pairs: page,
      total,
      skip,
      limit,
    };
    return response;
  }),

  // --- Create / update / delete a match ------------------------------------
  route('POST', '/reconciliation/reconcile', (req) => {
    const body = req.body as MatchRequest;
    const tx = transactions.find((t) => t.id === body.transaction_id);
    const inv = invoices.find((i) => i.id === body.invoice_id);
    if (!tx || !inv) return error(404, 'Transaction or invoice not found');

    const percentage = body.match_percentage ?? 100;
    const amount = Math.round(((inv.total_amount * percentage) / 100) * 100) / 100;
    const confidence = percentage >= 90 ? 'high' : percentage >= 70 ? 'medium' : 'low';

    const link: LinkRecord = {
      id: nextId('link'),
      transaction_id: tx.id,
      invoice_id: inv.id,
      match_percentage: percentage,
      match_success: 'manual',
      status: body.status ?? 'MATCHED',
      is_automatic: false,
      linked_at: nowISO(),
      match_reason: body.match_reason,
      notes: body.notes,
      is_committed: false,
      committed_at: null,
      mirage_accrec_id: null,
      preview: buildPreview({
        vendorName: inv.vendor.name,
        invoiceNumber: inv.invoice_number,
        invoiceDate: inv.invoice_date,
        vendorTaxId: inv.vendor.tax_id,
        amount,
        period: '07.2026',
        documentNumber: (links.length + 1).toString().padStart(10, '0'),
        confidence,
        reasoning: 'Ръчно съпоставяне от потребител.',
      }),
    };
    links.push(link);
    return toMatchResponse(link);
  }),

  route('PUT', '/reconciliation/link/:id', (req) => {
    const link = links.find((l) => l.id === req.params.id);
    if (!link) return error(404, 'Link not found');
    const body = req.body as MatchUpdateRequest;
    if (body.match_percentage !== undefined) link.match_percentage = body.match_percentage;
    if (body.match_reason !== undefined) link.match_reason = body.match_reason;
    if (body.notes !== undefined) link.notes = body.notes;
    if (body.status !== undefined) link.status = body.status;
    return toMatchResponse(link);
  }),

  route('PATCH', '/reconciliation/link/:id/accounts', (req) => {
    const link = links.find((l) => l.id === req.params.id);
    if (!link) return error(404, 'Link not found');
    const body = req.body as UpdateLinkAccountsRequest;
    if (body.debit_lines) link.preview.debit_lines = body.debit_lines;
    if (body.credit_lines) link.preview.credit_lines = body.credit_lines;
    if (body.debit_account !== undefined) link.preview.user_selected_debit_account = body.debit_account;
    if (body.credit_account !== undefined) link.preview.user_selected_credit_account = body.credit_account;
    return {
      link_id: link.id,
      user_selected_debit_account: link.preview.user_selected_debit_account ?? null,
      user_selected_credit_account: link.preview.user_selected_credit_account ?? null,
    };
  }),

  route('DELETE', '/reconciliation/unlink/:id', (req) => {
    const idx = links.findIndex((l) => l.id === req.params.id);
    if (idx === -1) return error(404, 'Link not found');
    links.splice(idx, 1);
    return { message: 'Връзката е премахната.' };
  }),

  route('DELETE', '/reconciliation/unmatch/:id', (req) => {
    const idx = links.findIndex((l) => l.id === req.params.id);
    if (idx === -1) return error(404, 'Link not found');
    const [removed] = links.splice(idx, 1);
    return { message: 'Съпоставянето беше премахнато.', link_id: removed.id };
  }),

  // --- Auto match -----------------------------------------------------------
  route('POST', '/reconciliation/auto-match', (req) => {
    const body = (req.body ?? {}) as { client_ids?: string[]; limit?: number };
    const clientIds = body.client_ids ?? [];
    const limit = body.limit ?? 100;

    const candidateTransactions = transactions
      .filter((tx) => matchesClient(tx.client_id, clientIds) && !linkForTransaction(tx.id))
      .slice(0, limit);

    const matches: MatchResponse[] = [];
    const skipped: { invoice_id: string; transaction_id: string; reason: string }[] = [];

    candidateTransactions.forEach((tx) => {
      const candidateInvoice = invoices.find(
        (inv) =>
          matchesClient(inv.client_id, clientIds) &&
          linksForInvoice(inv.id).length === 0 &&
          Math.abs(inv.total_amount - Math.abs(tx.amount)) < 0.01
      );

      if (!candidateInvoice) {
        skipped.push({
          invoice_id: '',
          transaction_id: tx.id,
          reason: 'Не е намерена фактура в толеранс за сумата.',
        });
        return;
      }

      const link: LinkRecord = {
        id: nextId('link'),
        transaction_id: tx.id,
        invoice_id: candidateInvoice.id,
        match_percentage: 100,
        match_success: 'auto',
        status: 'MATCHED',
        is_automatic: true,
        linked_at: nowISO(),
        is_committed: false,
        committed_at: null,
        mirage_accrec_id: null,
        preview: buildPreview({
          vendorName: candidateInvoice.vendor.name,
          invoiceNumber: candidateInvoice.invoice_number,
          invoiceDate: candidateInvoice.invoice_date,
          vendorTaxId: candidateInvoice.vendor.tax_id,
          amount: candidateInvoice.total_amount,
          period: '07.2026',
          documentNumber: (links.length + 1).toString().padStart(10, '0'),
          confidence: 'high',
          reasoning: 'Автоматично съвпадение по сума и контрагент.',
        }),
      };
      links.push(link);
      matches.push(toMatchResponse(link));
    });

    const response: AutoMatchResponse = {
      created: matches.length,
      attempted: candidateTransactions.length,
      matches,
      skipped,
      warnings: [],
    };
    return response;
  }),

  // --- Bulk commit / Mirage confirm -----------------------------------------
  route('POST', '/reconciliation/bulk-commit', (req) => {
    const body = req.body as BulkCommitRequest;
    const successful: CommitResult[] = [];
    const failed: CommitResult[] = [];

    body.link_ids.forEach((linkId) => {
      const link = links.find((l) => l.id === linkId);
      if (!link) {
        failed.push({ link_id: linkId, status: 'error', error_message: 'Записът не е намерен.' });
        return;
      }
      if (!link.is_committed) {
        link.is_committed = true;
        link.committed_at = nowISO();
        link.status = 'COMMITTED';
        link.mirage_accrec_id = nextAccrecId();
      }
      successful.push({ link_id: link.id, status: 'success', mirage_accrec_id: link.mirage_accrec_id ?? undefined });
    });

    const response: BulkCommitResponse = {
      message: `${successful.length} от ${body.link_ids.length} записа са осчетоводени успешно.`,
      successful_commits: successful,
      failed_commits: failed,
      summary: {
        total_requested: body.link_ids.length,
        successful: successful.length,
        failed: failed.length,
      },
    };
    return response;
  }),

  route('POST', '/mirage/reconcile/confirm', (req) => {
    const body = req.body as { link_id: string };
    const link = links.find((l) => l.id === body.link_id);
    if (!link) {
      const response: ReconcileConfirmResponse = {
        status: 'error',
        duration_seconds: 0.3,
        message: 'Записът не е намерен.',
        error: 'not_found',
      };
      return response;
    }

    if (!link.is_committed) {
      link.is_committed = true;
      link.committed_at = nowISO();
      link.status = 'COMMITTED';
      link.mirage_accrec_id = nextAccrecId();
    }

    const response: ReconcileConfirmResponse = {
      status: 'success',
      commit_id: nextId('commit'),
      mirage_accrec_id: link.mirage_accrec_id,
      duration_seconds: 1.4,
      message: 'Записът е осчетоводен успешно в Mirage.',
    };
    return response;
  }),

  // --- Delete transactions ---------------------------------------------------
  route('DELETE', '/reconciliation/transactions/:id', (req) => {
    const idx = transactions.findIndex((t) => t.id === req.params.id);
    if (idx === -1) return error(404, 'Транзакцията не е намерена.');
    if (linkForTransaction(transactions[idx].id)) {
      return error(409, 'Транзакцията е съпоставена и не може да бъде изтрита.');
    }
    transactions.splice(idx, 1);
    return { message: 'Транзакцията е изтрита.', transaction_id: req.params.id };
  }),

  route('DELETE', '/reconciliation/transactions/bulk', (req) => {
    const body = req.body as TransactionDeleteRequest;
    const deletedIds: string[] = [];
    const failedIds: string[] = [];
    const errors: string[] = [];

    body.transaction_ids.forEach((id) => {
      const idx = transactions.findIndex((t) => t.id === id);
      if (idx === -1) {
        failedIds.push(id);
        errors.push(`${id}: транзакцията не е намерена.`);
        return;
      }
      if (linkForTransaction(id)) {
        failedIds.push(id);
        errors.push(`${id}: транзакцията е съпоставена.`);
        return;
      }
      transactions.splice(idx, 1);
      deletedIds.push(id);
    });

    const response: TransactionDeleteResponse = {
      deleted_count: deletedIds.length,
      deleted_ids: deletedIds,
      failed_ids: failedIds,
      errors,
    };
    return response;
  }),

  // --- Scripted upload / processing status ----------------------------------
  route('POST', '/reconciliation/upload', (req) => {
    const body = req.body as FormData;
    const clientId = body instanceof FormData ? (body.get('client_id') as string | null) : null;
    const filename = 'unicredit_2026-07.xml';

    const existing = files.find((f) => f.original_filename === filename && f.client_id === (clientId ?? CLIENT_ID));
    if (existing) {
      const response: UploadBankStatementResponse = {
        message: 'Файлът вече е качен за този клиент.',
        duplicate: true,
        existing_file: {
          id: existing.id,
          original_filename: existing.original_filename,
          uploaded_at: existing.ingested_at,
          client_id: existing.client_id,
          transaction_count: existing.transaction_count,
          has_committed_links: existing.has_committed_links,
          committed_link_count: existing.committed_link_count,
        },
      };
      return response;
    }

    const fileId = `stmt-${Date.now()}`;
    files.push({
      id: fileId,
      original_filename: filename,
      mime_type: 'text/xml',
      size_bytes: 24576,
      source: 'unicredit',
      ingested_at: nowISO(),
      client_id: clientId ?? CLIENT_ID,
      client_name: CLIENT_NAME,
      transaction_count: 0,
      status: 'processing',
      has_committed_links: false,
      committed_link_count: 0,
    });

    uploadJobs.set(fileId, {
      fileId,
      startedAt: Date.now(),
      durationMs: 4200,
      finalized: false,
    });

    const response: UploadBankStatementResponse = {
      message: `Файлът ${BANK_NAME} (${BANK_IBAN}) е получен и се обработва.`,
      file_id: fileId,
      job_id: fileId,
      filename,
      processing_status: 'processing',
      duplicate: false,
    };
    return response;
  }),

  route('GET', '/reconciliation/upload/:fileId/status', (req) => {
    const job = uploadJobs.get(req.params.fileId);
    const fileRecord = files.find((f) => f.id === req.params.fileId);
    if (!job || !fileRecord) return error(404, 'Файлът не е намерен.');

    const { status, transactionsCount } = jobProgress(job);
    fileRecord.transaction_count = transactionsCount;

    const response: BankStatementProcessingStatus = {
      file_id: fileRecord.id,
      filename: fileRecord.original_filename,
      status,
      message:
        status === 'completed'
          ? `Обработката завърши. Заредени са ${transactionsCount} транзакции.`
          : `Обработват се транзакции… (${transactionsCount}/${JULY_TRANSACTIONS_COUNT})`,
      transactions_count: transactionsCount,
      uploaded_at: fileRecord.ingested_at,
    };
    return response;
  }),

  route('GET', '/reconciliation/upload/jobs/:jobId/status', (req) => {
    const job = uploadJobs.get(req.params.jobId);
    if (!job) return error(404, 'Задачата не е намерена.');
    const { status, transactionsCount } = jobProgress(job);
    return {
      job_id: req.params.jobId,
      status,
      progress: Math.round((transactionsCount / JULY_TRANSACTIONS_COUNT) * 100),
      current_step: status === 'completed' ? 'done' : 'importing_transactions',
    };
  }),

  // --- Processed files --------------------------------------------------------
  route('GET', '/reconciliation/files/detailed', (req) => {
    tickUploadJobs();
    const clientId = req.query.get('client_id');
    const skip = Number(req.query.get('skip') ?? 0);
    const limit = Number(req.query.get('limit') ?? 25);

    // Keep has_committed_links / committed_link_count in sync with live links.
    files.forEach((f) => {
      const fileTxIds = transactions.filter((t) => t.file_id === f.id).map((t) => t.id);
      const committedCount = links.filter((l) => fileTxIds.includes(l.transaction_id) && l.is_committed).length;
      f.has_committed_links = committedCount > 0;
      f.committed_link_count = committedCount;
    });

    let filtered = clientId ? files.filter((f) => f.client_id === clientId) : files;
    filtered = [...filtered].sort((a, b) => b.ingested_at.localeCompare(a.ingested_at));

    const total = filtered.length;
    const page = filtered.slice(skip, skip + limit);

    const response: ProcessedFilesResponse = {
      files: page,
      total,
      skip,
      limit,
    };
    return response;
  }),

  route('GET', '/reconciliation/files/:id/delete-check', (req) => {
    const file = files.find((f) => f.id === req.params.id);
    if (!file) return error(404, 'Файлът не е намерен.');

    const fileTxIds = transactions.filter((t) => t.file_id === file.id).map((t) => t.id);
    const committedCount = links.filter((l) => fileTxIds.includes(l.transaction_id) && l.is_committed).length;

    const response: FileDeleteCheckResponse = {
      can_delete: true,
      has_committed_links: committedCount > 0,
      committed_link_count: committedCount,
      transaction_count: fileTxIds.length,
      warnings:
        committedCount > 0
          ? ['Файлът съдържа транзакции, осчетоводени в Mirage. Изтриването няма да storno-ира записите.']
          : [],
    };
    return response;
  }),

  route('DELETE', '/reconciliation/files/:id', (req) => {
    const file = files.find((f) => f.id === req.params.id);
    if (!file) return error(404, 'Файлът не е намерен.');

    const fileTxIds = transactions.filter((t) => t.file_id === file.id).map((t) => t.id);
    fileTxIds.forEach((txId) => {
      const linkIdx = links.findIndex((l) => l.transaction_id === txId);
      if (linkIdx !== -1) links.splice(linkIdx, 1);
      const txIdx = transactions.findIndex((t) => t.id === txId);
      if (txIdx !== -1) transactions.splice(txIdx, 1);
    });

    const fileIdx = files.findIndex((f) => f.id === file.id);
    files.splice(fileIdx, 1);

    const response: FileDeleteResponse = {
      deleted: true,
      file_id: file.id,
      transactions_deleted: fileTxIds.length,
      warnings: [],
    };
    return response;
  }),

  route('POST', '/reconciliation/files/:id/reprocess', (req) => {
    const file = files.find((f) => f.id === req.params.id);
    if (!file) return error(404, 'Файлът не е намерен.');

    const fileTxIds = transactions.filter((t) => t.file_id === file.id).map((t) => t.id);
    const committedCount = links.filter((l) => fileTxIds.includes(l.transaction_id) && l.is_committed).length;
    const force = req.query.get('force') === 'true';

    if (committedCount > 0 && !force) {
      const response: FileReprocessResponse = {
        success: false,
        message: 'Файлът съдържа осчетоводени записи. Потвърдете, за да продължите.',
        transactions_deleted: 0,
        warnings: ['Осчетоводените записи в Mirage няма да бъдат сторнирани.'],
      };
      return response;
    }

    fileTxIds.forEach((txId) => {
      const linkIdx = links.findIndex((l) => l.transaction_id === txId);
      if (linkIdx !== -1) links.splice(linkIdx, 1);
      const txIdx = transactions.findIndex((t) => t.id === txId);
      if (txIdx !== -1) transactions.splice(txIdx, 1);
    });
    const fileIdx = files.findIndex((f) => f.id === file.id);
    files.splice(fileIdx, 1);

    const response: FileReprocessResponse = {
      success: true,
      message: 'Файлът беше премахнат и може да бъде качен отново.',
      transactions_deleted: fileTxIds.length,
      warnings: [],
    };
    return response;
  }),
];
